import { Box, Divider, Flex, Text, Tooltip, useTheme } from '@chakra-ui/react';
import React, { Fragment } from 'react';
import { InfoIcon, RightIcon } from '../../../icons';
import { StyledLink } from '../../../StyledLink';
import GoalLoadingCell from '../LoadingCell/GoalLoadingCell';
import GoalDetails from '../../../GoalsTable/GoalDetails';
import { useTeamTableContext } from '../../context/TeamTableContext';
import { GoalStatusSummaryItem } from '../../types/team-table.types';

interface KeyResultsColProps {
  goalScope: 'CONTRIBUTOR' | 'TEAM';
  associatedIds?: string[];
  teamParentMap?: Record<string, string | null>;
  goalsTeamDataLoading?: boolean;
  expanded: string[];
  goalStatusSummary: GoalStatusSummaryItem[];
  goalStatusSummaryLoading: boolean;
  rowHeight?: string;
  hasRightBorder?: boolean;
}
function KeyResultsCol({
  goalScope,
  goalsTeamDataLoading,
  goalStatusSummary,
  goalStatusSummaryLoading,
  expanded,
  teamParentMap,
  rowHeight,
  hasRightBorder,
}: KeyResultsColProps) {
  const { colors } = useTheme();
  return (
    <Flex
      direction="column"
      position="sticky"
      left={0}
      bg="white"
      h={'fit-content'}
      minW="370px"
      zIndex={79}
    >
      <Flex
        bg="#FBFBFB"
        position="sticky"
        top={0}
        borderLeft="1px solid #E0E0E0"
        borderRight={hasRightBorder ? '1px solid #E0E0E0' : undefined}
        align="center"
        minH="52px"
        px={4}
        zIndex={50}
        w={'full'}
      >
        <Flex w="full" align="center" gap={1}>
          <Text
            fontSize="sm"
            fontFamily="heading"
            fontWeight="medium"
            color="text.secondary2"
            flex={1}
          >
            Key Results
          </Text>{' '}
          <Tooltip
            label="Measures how often a Key Result's target was met within its timeframe. E.g., if a KR met its target in 4 out of 5 months, its success is 80%. This shows the consistency of achieving a specific outcome. Goal/KR Progress levels (On Track, Behind, At Risk) are based on org-wide settings."
            hasArrow
          >
            <Box>
              <InfoIcon />
            </Box>
          </Tooltip>
        </Flex>
      </Flex>

      <Divider />

      {goalsTeamDataLoading ||
      goalStatusSummaryLoading ||
      goalStatusSummary?.length === 0
        ? [...Array(10)].map((_, index) => (
            <GoalLoadingCell
              goalScope={goalScope}
              key={index}
              rowHeight={rowHeight}
            />
          ))
        : goalStatusSummary
            ?.filter(
              (row: GoalStatusSummaryItem) =>
                expanded.includes(teamParentMap?.[row.name] || '') ||
                teamParentMap?.[row.name] === null
            )
            ?.map((goalData: GoalStatusSummaryItem) => (
              <Fragment key={goalData?.name}>
                <Flex
                  minH={
                    rowHeight ||
                    (goalScope === 'CONTRIBUTOR'
                      ? '164px'
                      : goalScope === 'TEAM'
                      ? '184px'
                      : '200px')
                  }
                  p={3}
                  bg="white"
                  borderRight={hasRightBorder ? '1px solid #E0E0E0' : undefined}
                  borderLeft="1px solid"
                  borderColor={'text.tertiary'}
                  gap={3}
                  direction={'column'}
                >
                  <Flex gap={8}>
                    <GoalDetails
                      data={goalData?.keyResultStatusData}
                      title="Key Result(s)"
                      origin={goalScope === 'TEAM' ? 'team-table' : ''}
                      isLoading={goalStatusSummaryLoading}
                    />
                  </Flex>
                </Flex>
                <Divider />
              </Fragment>
            ))}
    </Flex>
  );
}

export default KeyResultsCol;
